import { entityExists, query } from 'bitecs';
import { Config } from '@src/core/Config';
import { GameEvent } from '@src/core/EventQueue';
import { killOrbiters, spawnFx, spawnOrbiter, stripOffhand } from '@src/ecs/archetypes';
import {
  BossTag,
  Damage,
  Dying,
  Enemy,
  EnemyKind,
  Health,
  Knockback,
  OFFHAND_NONE,
  OFFHAND_SHIELD,
  Orbiter,
  Player,
  Position,
  PrevPosition,
  Radius,
  Spawner,
  Sprite,
  SpriteId,
} from '@src/ecs/components';
import type { GameSystem } from '@src/ecs/System';
import { emitParticles, ParticleKind, type GameContext } from '@src/ecs/world';
import { dealDamage } from '@src/game/Combat';

const CANDIDATES = 256;

/**
 * Rune Ward orbiters and Gale Step timer. The ward is a parallel off-hand:
 * primary WeaponId is never touched. Blades persist across rooms until the
 * duration ends or the wizard dies.
 */
export class OffhandSystem implements GameSystem {
  private ctx!: GameContext;
  private readonly candidates = new Int32Array(CANDIDATES);
  private readonly nextHit = new Float32Array(Config.sim.maxEntities);
  private sparkAcc = 0;

  init(ctx: GameContext): void {
    this.ctx = ctx;
  }

  destroy(): void {}

  update(dt: number): void {
    const ctx = this.ctx;
    const shield = Config.pickups.shield;
    const players = query(ctx.world, [Player, Position]);

    for (let i = 0; i < players.length; i++) {
      const pid = players[i];
      if (Player.swiftTime[pid] > 0) {
        Player.swiftTime[pid] -= dt;
        if (Player.swiftTime[pid] <= 0) {
          Player.swiftTime[pid] = 0;
          ctx.events.push(GameEvent.SwiftExpired, pid, 0, Position.x[pid], Position.y[pid]);
        }
      }

      if (Player.dead[pid]) {
        if (Player.offhand[pid] !== OFFHAND_NONE) {
          killOrbiters(ctx, pid);
          stripOffhand(pid);
        }
        continue;
      }

      if (Player.offhand[pid] === OFFHAND_SHIELD) {
        Player.offhandTime[pid] -= dt;
        if (Player.offhandTime[pid] <= 0) {
          killOrbiters(ctx, pid);
          stripOffhand(pid);
          ctx.events.push(GameEvent.OffhandExpired, pid, 0, Position.x[pid], Position.y[pid]);
          continue;
        }
        this.ensureBlades(pid);
      } else if (Player.offhand[pid] !== OFFHAND_NONE) {
        killOrbiters(ctx, pid);
        stripOffhand(pid);
      }
    }

    this.sparkAcc += dt;
    const spark = this.sparkAcc >= 0.055;
    if (spark) this.sparkAcc = 0;

    const blades = query(ctx.world, [Orbiter, Position, Radius, Damage, Knockback]);
    for (let i = 0; i < blades.length; i++) {
      const b = blades[i];
      if (Dying.flag[b]) continue;
      const owner = Orbiter.owner[b];
      if (
        owner < 0 ||
        !entityExists(ctx.world, owner) ||
        Player.dead[owner] ||
        Player.offhand[owner] !== OFFHAND_SHIELD
      ) {
        killOrbiters(ctx, owner);
        continue;
      }

      Orbiter.angle[b] += Orbiter.spin[b] * dt;
      const a = Orbiter.angle[b];
      const r = Orbiter.radius[b];
      PrevPosition.x[b] = Position.x[b];
      PrevPosition.y[b] = Position.y[b];
      Position.x[b] = Position.x[owner] + Math.cos(a) * r;
      Position.y[b] = Position.y[owner] + Math.sin(a) * r * 0.72;
      Sprite.rot[b] = a + Math.PI * 0.5;
      const low = Player.offhandTime[owner] < 3;
      Sprite.alpha[b] = low ? 0.45 + Math.sin(ctx.time * 14) * 0.25 : 0.92 + Math.sin(ctx.time * 8 + a) * 0.08;
      Sprite.scale[b] = 0.68 + Math.sin(ctx.time * 10 + a) * 0.06;
      if (Orbiter.cool[b] > 0) Orbiter.cool[b] -= dt;

      if (spark) {
        emitParticles(
          ctx.fx,
          ParticleKind.PickupSparkle,
          Position.x[b],
          Position.y[b],
          -Math.sin(a),
          Math.cos(a),
          0.22,
          shield.tint,
        );
      }

      this.hitNear(b, owner, shield.targetIframe);
    }

    this.tickSwiftTrail(dt, players);
  }

  private ensureBlades(owner: number): void {
    const ctx = this.ctx;
    const need = Config.pickups.shield.blades;
    const blades = query(ctx.world, [Orbiter]);
    let live = 0;
    for (let i = 0; i < blades.length; i++) {
      if (Orbiter.owner[blades[i]] === owner && !Dying.flag[blades[i]]) live++;
    }
    for (let i = live; i < need; i++) spawnOrbiter(ctx, owner, (i / need) * Math.PI * 2);
  }

  private hitNear(blade: number, owner: number, iframe: number): void {
    if (Orbiter.cool[blade] > 0) return;
    const ctx = this.ctx;
    const bx = Position.x[blade];
    const by = Position.y[blade];
    const br = Radius.r[blade];
    const now = ctx.time;
    const dmg = Damage.amount[blade];
    const kb = Knockback.force[blade];
    const tint = Config.pickups.shield.tint;

    const bosses = query(ctx.world, [BossTag, Position, Radius, Health]);
    for (let i = 0; i < bosses.length; i++) {
      if (this.tryHit(blade, owner, bosses[i], bx, by, br, dmg, 1, kb, now, iframe, tint)) return;
    }

    const spawners = query(ctx.world, [Spawner, Position, Radius, Health]);
    for (let i = 0; i < spawners.length; i++) {
      if (this.tryHit(blade, owner, spawners[i], bx, by, br, dmg, Damage.spawnerMult[blade], kb, now, iframe, tint)) return;
    }

    const n = ctx.grid.gather(bx, by, br + 0.9, this.candidates);
    for (let k = 0; k < n; k++) {
      const e = this.candidates[k];
      if (Enemy.kind[e] === EnemyKind.Boss) continue;
      if (this.tryHit(blade, owner, e, bx, by, br, dmg, 1, kb, now, iframe, tint)) return;
    }
  }

  private tryHit(
    blade: number,
    owner: number,
    target: number,
    bx: number,
    by: number,
    br: number,
    dmg: number,
    spawnerMult: number,
    kb: number,
    now: number,
    iframe: number,
    tint: number,
  ): boolean {
    const ctx = this.ctx;
    if (Dying.flag[target] || Health.hp[target] <= 0) return false;
    if (now < this.nextHit[target]) return false;
    const dx = Position.x[target] - bx;
    const dy = Position.y[target] - by;
    const rr = br + Radius.r[target];
    if (dx * dx + dy * dy > rr * rr) return false;
    const d = Math.hypot(dx, dy) || 1;
    if (!dealDamage(ctx, target, dmg, (dx / d) * kb, (dy / d) * kb, spawnerMult, 0)) return false;
    this.nextHit[target] = now + iframe;
    Orbiter.cool[blade] = Config.pickups.shield.hitCooldown;
    Sprite.flash[blade] = 1;
    spawnFx(ctx, SpriteId.Spark, bx, by, ctx.rng.next() * Math.PI * 2, 0.55, tint, 0.86);
    emitParticles(ctx.fx, ParticleKind.HitSpark, bx, by, dx / d, dy / d, 0.45, tint);
    ctx.events.push(GameEvent.OffhandHit, owner, target, bx, by);
    return true;
  }

  private tickSwiftTrail(dt: number, players: ArrayLike<number>): void {
    const ctx = this.ctx;
    const tint = Config.pickups.swift.tint;
    for (let i = 0; i < players.length; i++) {
      const pid = players[i];
      if (Player.dead[pid] || Player.swiftTime[pid] <= 0) continue;
      if (!ctx.rng.chance(dt * 14)) continue;
      emitParticles(
        ctx.fx,
        ParticleKind.Embers,
        Position.x[pid],
        Position.y[pid] - 0.15,
        -Player.moveX[pid],
        -Player.moveY[pid],
        0.35,
        tint,
      );
    }
  }
}
